import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Card } from '@material-ui/core';
import cn from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { get } from 'lodash';
import Img from 'react-image';
import { Phone, MailOutline, Language } from '@material-ui/icons';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { colors } from '../../constants/styleConstants';
import { useTranslation } from '../../src/i18n';
import EditIcon from '../Icons/EditIcon';
import { TP } from '../../constants/consts';
import Button from '../Button';
import TitledBlock from '../TitledBlock';
import TitleBlock from '../EditComponents/TitleBlock';
import TextButton from '../TextButton';
import DeleteFbIcon from '../Icons/DeleteFbIcon';
import EllipsisText from '../EllipsisText';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    backgroundColor: colors.white,
    border: `1px solid ${colors.linesGray}`,
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
    '& > div': {
      padding: theme.spacing(2),
    },
  },
  img: {
    width: 130,
  },
  contentContainer: {
    flexGrow: 1,
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      order: 3,
    },
  },
  venueValues: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
  },
  actionButton: {
    maxWidth: 160,
    height: 50,
  },
  titledBlock: {
    width: '50%',
  },
}));

const contactFields = {
  phone: {
    property: 'phone',
    icon: Phone,
  },
  email: {
    property: 'email',
    icon: MailOutline,
  },
  url: {
    property: 'url',
    icon: Language,
  },
};

const iconStyle = { fill: colors.blue, fontSize: '1rem', marginRight: '2px' };
const contactInfoStyle = { display: 'flex', alignItems: 'center' };

const labels = {
  country: {
    title: `${TP}.m_COUNTRY`,
    content: venue => get(venue, 'country.name'),
  },
  city: {
    title: `${TP}.m_CITY`,
    content: venue => get(venue, 'city.name'),
  },
  address: {
    title: `${TP}.m_ADDRESS`,
    content: venue => get(venue, 'address'),
  },
  contact: {
    title: `${TP}.CONTACT_INFORMATION`,
    content: venue => (
      <div>
        {Object.values(contactFields).map(contact => {
          const data = get(venue, contact.property);
          return data ? (
            <div key={contact.property} style={contactInfoStyle}>
              {React.createElement(contact.icon, { style: iconStyle })}{' '}
              {contact.property === 'url' ? (
                <EllipsisText text={get(venue, contact.property)} />
              ) : (
                <span>{get(venue, contact.property)}</span>
              )}
            </div>
          ) : null;
        })}
      </div>
    ),
  },
};

const EditVenueCard = ({ onEditClick, venue, className, onDelete, onDefaultCheck }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const src = get(venue, 'logo.medium');
  const venueName = get(venue, 'name');
  const stages = get(venue, 'stages');
  const stagesAmount =
    Array.isArray(stages) && stages.length > 0 ? t(`${TP}.STAGES_AMOUNT`, { number: stages.length }) : '';

  return (
    <Card className={cn(classes.root, className)}>
      <div className={classes.container}>
        <div>
          <Img className={classes.img} src={src} alt="Venue image" />
        </div>
        <div className={classes.contentContainer}>
          <FormControlLabel
            id={`venue_${venue.id}`}
            control={
              <Checkbox
                checked={venue.isDefault}
                onChange={e => onDefaultCheck(e?.target?.checked)}
                color="secondary"
              />
            }
            label={t(`${TP}.FN_MARK_VENUE_DEFAULT`)}
          />
          <TitleBlock title={venueName} subtitle={stagesAmount} />
          <div className={classes.venueValues}>
            {Object.values(labels).map(label => (
              <TitledBlock key={label.title} title={t(label.title)} className={classes.titledBlock}>
                {label.content(venue)}
              </TitledBlock>
            ))}
          </div>
        </div>
        <div className={classes.actionContainer}>
          <Button
            startIcon={<EditIcon />}
            onClick={() => onEditClick(venue)}
            color="default"
            className={classes.actionButton}
          >
            {t(`${TP}.m_EDIT`)}
          </Button>
          <TextButton className={classes.actionButton} startIcon={<DeleteFbIcon />} onClick={() => onDelete(venue)}>
            {t(`${TP}.FN_REMOVE`)}
          </TextButton>
        </div>
      </div>
    </Card>
  );
};

EditVenueCard.propTypes = {
  onEditClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDefaultCheck: PropTypes.func.isRequired,
  venue: PropTypes.object.isRequired,
  className: PropTypes.string,
};

export default memo(EditVenueCard);
